import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Grid2 from '@mui/material/Grid2';
import ExecutiveSummary from './sections/ExecutiveSummary';
import SubcontractorDetails from './sections/SubcontractorDetails';
import ProjectInformation from './sections/ProjectInformation';
import StyledAccordion from './components/StyledAccordion';
import TenderRecommendationHeader from './components/TenderRecommendationHeader';
import i18next from 'v2/helpers/i18n';
import PackageInformation from './sections/PackageInformation';
import PricingSummary from './sections/PricingSummary';
import SummaryAndRecommendations from './sections/SummaryAndRecommendations';
import Attachments from './sections/Attachments';
import LOIDetails from './sections/LOIDetails';
import {
  mapLoiDetailsToLetterOfIntentPayload,
  mapTenderRecommendationToLoiDetails,
} from './helpers/loiApiMapper';
import PdfDialog from 'v2/apps/clink/pages/tender-recommendations/PdfDialog';
import { connect } from 'react-redux';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useParams } from 'react-router-dom';
import { httpHelperV2 } from 'v2/services/httpHelper';
import { useContext } from 'hooks/context';
import LogsModal from 'v2/apps/shared/components/logs-modal';
import TrRejectionFeedbackBanner from './components/TrRejectionFeedbackBanner';

const TenderRecommendationForm = ({
  contextType = 'clink',
  project,
  dispatch,
  tenderRecommendationForm,
  tenderRecommendationById,
  tenderRecommendations,
  pricingSummary,
  tenderRecommendationLogsData,
}) => {
  const context = useContext(contextType);
  const params = useParams();
  const { actions } = context;
  const pid = project?.data?.id;
  const { recommendationId } = params;
  const tender_recommendation_id =
    recommendationId ?? tenderRecommendationForm?.id;

  const [expanded, setExpanded] = useState({
    executive: false,
    subcontractor: false,
    project: false,
    package_information: false,
    pricing_summary: false,
    loi_details: false,
    summary_and_recommendations: false,
    attachments: false,
  });
  const [recommendedSubcontractorId, setRecommendedSubcontractorId] = useState(null);

  const [openPdf, setOpenPdf] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isSaving, setIsSaving] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [approvalRequestSent, setApprovalRequestSent] = useState(false);
  const [localForecasts, setLocalForecasts] = useState(null);
  const [localLoiDetails, setLocalLoiDetails] = useState(null);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const TenderData = useMemo(() => {
    if (!tenderRecommendationById) return null;

    const dataId = tenderRecommendationById?.tender_recommendation_id;
    const currentId = parseInt(tender_recommendation_id, 10);
    return dataId === currentId ? tenderRecommendationById : null;
  }, [tenderRecommendationById, tender_recommendation_id]);

  const assignedApprovers = useMemo(() => {
    const trFromList = tenderRecommendations?.find(
      (tr) =>
        String(tr.tender_recommendation_id) ===
        String(tender_recommendation_id),
    );
    return trFromList?.assigned_approvers ?? [];
  }, [tenderRecommendations, tender_recommendation_id]);

  const isLOIPackage = TenderData?.has_loi;

  // Memoized on TenderData so `loi_details` keeps a stable reference and
  // doesn't re-trigger LOIDetails's sync effect on every render.
  const loiDetailsFromResponse = useMemo(
    () => mapTenderRecommendationToLoiDetails(TenderData),
    [TenderData],
  );
  const loiSectionData = useMemo(
    () => ({ ...TenderData, loi_details: loiDetailsFromResponse }),
    [TenderData, loiDetailsFromResponse],
  );

  const fetchTenderRecommendationById = useCallback(async () => {

    if (pid && tender_recommendation_id) {
      dispatch(
        await actions.getTenderRecommendationById({
          project_id: pid,
          tid: tender_recommendation_id,
        }),
      );
    }
  }, [actions, dispatch, pid, tender_recommendation_id]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
  };

  const viewLogs = useCallback(() => {
    if (pid && tender_recommendation_id) {
      dispatch(
        actions.getAuditLogsTenderRecommendationById({
          project_id: pid,
          tender_recommendation_id,
        }),
      )
        .then(() => setLogsModalOpen(true))
        .catch((error) => {
          const errorMessage = error?.response?.data?.message;
          showSnackbar(errorMessage, 'error');
        });
    }
  }, [pid, tender_recommendation_id, dispatch, actions]);

  const handleRequestApproval = useCallback(
    ({ selectedApprovers }) => {
      if (pid && tender_recommendation_id) {
        return dispatch(
          actions.assignTenderRecommendationApprover({
            project_id: pid,
            tid: tender_recommendation_id,
            data: selectedApprovers,
          }),
        )
          .unwrap()
          .then(() => {
            fetchTenderRecommendationById();
          });
      }
      return undefined;
    },
    [
      actions,
      dispatch,
      pid,
      tender_recommendation_id,
      fetchTenderRecommendationById,
    ],
  );

  useEffect(() => {
    fetchTenderRecommendationById();
  }, [fetchTenderRecommendationById]);

  useEffect(() => {
    if (pid) {
      dispatch(actions.getTenderRecommendations({ project_id: pid }));
    }
  }, [actions, dispatch, pid]);

  useEffect(() => {

    const data = TenderData;

    const subcontractorId = data?.subcontractor?.id;
    setRecommendedSubcontractorId(subcontractorId);
    if (subcontractorId) {
      dispatch(
        actions.fetchBySubcontractorId({ subcontractor_id: subcontractorId }),
      );
    }
  }, [TenderData, dispatch, actions]);

  useEffect(() => {
    const data = TenderData;
    const subcontractorId = data?.subcontractor?.id;
    if (subcontractorId) {
      dispatch(actions.fetchTeamApi(pid));
    }
  }, [TenderData, pid, dispatch, actions]);

  useEffect(() => {
    const data = TenderData;
    const packageId = data?.package_id;
    if (packageId) {
      dispatch(
        actions.getTenderRecommendationPricingSummary({
          project_id: pid,
          package_id: packageId,
        }),
      );
    }
  }, [TenderData, actions, dispatch, pid]);

  const handleAccordionChange = (section) => {
    setExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleOpenPdf = async () => {
    if (!project?.data?.id || !tender_recommendation_id) return;
    setLoading(true);
    httpHelperV2({
      url: `project/${project?.data?.id}/tender_recommendation/${tender_recommendation_id}/report/generate`,
      method: 'POST',
    })
      .then((result) => {
        setPdfUrl(
          result?.url ? `${result.url}#toolbar=0&navpanes=0&scrollbar=0` : '',
        );
        setOpenPdf(true);
        setLoading(false);
      })
      .catch(() => {
        showSnackbar(
          i18next.t('no-tender-recommendations-report-exists'),
          'error',
        );
        setLoading(false);
      });
  };

  const handleAcknowledge = useCallback(async () => {
    setAcknowledging(true);
    try {
      await dispatch(
        actions.updateTenderRecommendationById({
          project_id: pid,
          tid: tender_recommendation_id,
          data: { status: 'Draft' },
        }),
      ).unwrap();
      fetchTenderRecommendationById();
      showSnackbar(i18next.t('rejection-acknowledged-success'), 'success');
    } catch (error) {
      showSnackbar(
        error?.response?.data?.message ||
        i18next.t('rejection-acknowledged-error'),
        'error',
      );
    } finally {
      setAcknowledging(false);
    }
  }, [
    dispatch,
    actions,
    pid,
    tender_recommendation_id,
    fetchTenderRecommendationById,
  ]);

  const handleSaveAsDraft = async () => {
    if (!TenderData?.tender_recommendation_id) return;
    setIsSaving(true);
    try {
      const response = await dispatch(
        actions.saveAsDraftTenderRecommendationById({
          project_id: pid,
          tid: TenderData?.tender_recommendation_id,
          data: {
            exec_summary: TenderData?.exec_summary,
            subcontractor_user_id: TenderData?.subcontractor_user_id,
            final_comment: TenderData?.final_comment,
            forecasts:
              (localForecasts ?? pricingSummary)?.map((item) => ({
                transaction_id: item?.transaction_id,
                forecast: item?.forecast,
              })) || [],
            ...(isLOIPackage
              ? mapLoiDetailsToLetterOfIntentPayload(localLoiDetails)
              : {}),
          },
        }),
      );
      showSnackbar(response?.payload?.message, 'success');
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsSaving(false);
    }
  };
  const accordions = [
    {
      key: 'executive',
      titleKey: 'executive-summary',
      visible: true,
      content: (
        <ExecutiveSummary
          data={TenderData}
          projectId={pid}
          dispatch={dispatch}
          actions={actions}
          approvalRequestSent={approvalRequestSent}
        />
      ),
    },
    {
      key: 'subcontractor',
      titleKey: 'recommended-subcontractor-details',
      visible: true,
      content: <SubcontractorDetails approvalRequestSent={approvalRequestSent} />,
    },
    {
      key: 'project',
      titleKey: 'project-information',
      visible: true,
      content: <ProjectInformation project={project} />,
    },
    {
      key: 'package_information',
      // Pre-existing bug: reads `expanded.package`, not its own key - left as-is.
      expandedKey: 'package',
      titleKey: 'package-information',
      visible: true,
      content: <PackageInformation />,
    },
    {
      key: 'loi_details',
      titleKey: 'loi-details',
      visible: isLOIPackage,
      content: (
        <LOIDetails
          data={loiSectionData}
          projectId={pid}
          dispatch={dispatch}
          actions={actions}
          approvalRequestSent={approvalRequestSent}
          onLoiDetailsChange={setLocalLoiDetails}
        />
      ),
    },
    {
      key: 'pricing_summary',
      // Same pre-existing `expanded.package` bug as package_information.
      expandedKey: 'package',
      titleKey: 'pricing-summary',
      visible: true,
      content: (
        <PricingSummary
          approvalRequestSent={approvalRequestSent}
          onForecastsChange={setLocalForecasts}
          recommendedSubcontractorId={recommendedSubcontractorId}
        />
      ),
    },
    {
      key: 'summary_and_recommendations',
      // Same pre-existing `expanded.package` bug as package_information.
      expandedKey: 'package',
      titleKey: 'summary-and-recommendations',
      visible: true,
      content: (
        <SummaryAndRecommendations
          data={TenderData}
          projectId={pid}
          dispatch={dispatch}
          actions={actions}
          approvalRequestSent={approvalRequestSent}
        />
      ),
    },
    {
      key: 'attachments',
      titleKey: 'attachments',
      visible: true,
      content: (
        <Attachments
          data={TenderData}
          projectId={pid}
          dispatch={dispatch}
          actions={actions}
          approvalRequestSent={approvalRequestSent}
          showSnackbar={showSnackbar}
        />
      ),
    },
  ];

  const visibleAccordions = accordions.filter((accordion) => accordion.visible);

  return (
    <Grid2 container spacing={0} sx={{ flexDirection: 'column' }}>
      <TenderRecommendationHeader
        setOpenPdf={handleOpenPdf}
        loading={loading}
        setOpenLogModal={viewLogs}
        saveDraft={handleSaveAsDraft}
        isSaving={isSaving}
        handleRequestApproval={handleRequestApproval}
        tenderData={TenderData}
        approvalRequestSent={approvalRequestSent}
        setApprovalRequestSent={setApprovalRequestSent}
      />
      <TrRejectionFeedbackBanner
        tenderData={TenderData}
        assignedApprovers={assignedApprovers}
        countryCode={project?.data?.country?.code}
        onAcknowledge={handleAcknowledge}
        acknowledging={acknowledging}
      />
      <Grid2 sx={{ px: 3 }}>
        {visibleAccordions.map((accordion, index) => (
          <StyledAccordion
            key={accordion.key}
            data-testid="tr-accordion-executive"
            title={`${index + 1}. ${i18next.t(accordion.titleKey)}`}
            expanded={expanded[accordion.expandedKey ?? accordion.key]}
            onChange={() => handleAccordionChange(accordion.key)}
          >
            {accordion.content}
          </StyledAccordion>
        ))}
      </Grid2>

      <PdfDialog
        open={openPdf}
        onClose={() => setOpenPdf(false)}
        pdfUrl={pdfUrl}
        title={i18next.t('tender-recommendations-report')}
        projectId={project?.data?.id}
        recommendationId={tender_recommendation_id}
      />

      <LogsModal
        open={logsModalOpen}
        onClose={() => setLogsModalOpen(false)}
        allLogs={tenderRecommendationLogsData?.logs || []}
        headerTitle={i18next.t('tender-recommendations-logs')}
        countryCode={project?.data?.country?.code}
        entity="TR"
        entity_no={tenderRecommendationLogsData?.entity_no}
        package_name={tenderRecommendationLogsData?.package_name}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(snackbarMessage)}
        autoHideDuration={5000}
        onClose={() => setSnackbarMessage('')}
      >
        <MuiAlert
          onClose={() => setSnackbarMessage('')}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Grid2>
  );
};
const mapStateToProps = (state) => ({
  project: state.project,
  pricingSummary: state.tenderRecommendation.pricingSummary,
  tenderRecommendationById: state.tenderRecommendation.tenderRecommendationById,
  tenderRecommendationForm: state.tenderRecommendation.tenderRecommendationForm,
  tenderRecommendations: state.tenderRecommendation.recommendations,
  tenderRecommendationLogsData:
    state.tenderRecommendation.tenderRecommendationLogs,
});
export default connect(mapStateToProps)(TenderRecommendationForm);
